import { useCallback, useEffect, useState } from 'react'
import { Link } from 'react-router-dom'

import { OnecSyncDiff } from '@/components/settings/OnecSyncDiff'
import { SwitchTrack } from '@/components/ui/Switch'
import {
  getOnecSyncDiff,
  getOnecSyncSettings,
  getOnecSyncStatus,
  requestOnecSync,
  updateOnecSyncSettings,
} from '@/lib/api/client/onec-sync'
import type {
  OnecPieceDiff,
  OnecSyncerStatus,
  OnecSyncFailureReason,
  OnecSyncRun,
  OnecSyncSettings as OnecSyncSwitches,
} from '@/lib/api/schemas/onec-sync.schema'
import { unavailableReasonText } from '@/lib/warehouse/labels'

const HEALTH_LABEL: Record<OnecSyncerStatus['health'], string> = {
  alive: 'На связи',
  silent: 'Не отвечает больше 15 минут',
  never_seen: 'Ещё ни разу не выходил на связь',
}

const REQUEST_LABEL = {
  pending: 'ждёт, когда gold01 заберёт запрос',
  claimed: 'gold01 выполняет синхронизацию',
  done: 'выполнен',
  failed: 'завершился ошибкой',
  expired: 'gold01 не забрал запрос',
} as const

const OUTCOME_LABEL: Record<OnecSyncRun['outcome'], string> = {
  completed: 'Изменения переданы',
  unchanged: 'Без изменений',
  failed: 'Ошибка',
}

const FAILURE_LABEL: Record<OnecSyncFailureReason, string> = {
  onec_com_unavailable: 'COM-коннектор 1С недоступен',
  onec_base_unreachable: 'база 1С не открылась',
  onec_extract_failed: 'выгрузка из 1С прервалась',
  onec_extract_invalid: 'выгрузка из 1С не прошла проверку',
  syncer_internal_error: 'внутренняя ошибка синкера',
}

// Only the reasons that name a Forge piece rather than a 1C row; the rest read through the shared table.
const COST_MISSING_LABEL: Record<string, string> = {
  uin_not_recorded: 'у изделия нет УИН, себестоимость из 1С к нему не привязать',
  cost_not_synced: 'себестоимость из 1С ещё не загружена',
}

function costMissingLabel(code: string): string {
  return COST_MISSING_LABEL[code] ?? unavailableReasonText(code)
}

function when(value: string): string {
  return new Intl.DateTimeFormat('ru-RU', { dateStyle: 'short', timeStyle: 'short', timeZone: 'Europe/Riga' }).format(new Date(value))
}

function message(reason: unknown, fallback: string): string {
  return reason instanceof Error && reason.message.length > 0 ? reason.message : fallback
}

export function OnecSyncSettings() {
  const [status, setStatus] = useState<OnecSyncerStatus | null>(null)
  const [switches, setSwitches] = useState<OnecSyncSwitches | null>(null)
  const [diff, setDiff] = useState<OnecPieceDiff | null>(null)
  const [loading, setLoading] = useState(true)
  const [busy, setBusy] = useState(false)
  const [notice, setNotice] = useState<string | null>(null)
  const [error, setError] = useState<string | null>(null)

  const load = useCallback(async (signal?: AbortSignal) => {
    try {
      const [nextStatus, nextSwitches, nextDiff] = await Promise.all([
        getOnecSyncStatus(signal),
        getOnecSyncSettings(signal),
        getOnecSyncDiff({}, signal),
      ])
      setStatus(nextStatus)
      setSwitches(nextSwitches)
      setDiff(nextDiff)
      setError(null)
    } catch (reason: unknown) {
      if (!signal?.aborted) setError(message(reason, 'Не удалось загрузить состояние синхронизации с 1С.'))
    } finally {
      if (!signal?.aborted) setLoading(false)
    }
  }, [])

  useEffect(() => {
    const controller = new AbortController()
    void load(controller.signal)
    return () => controller.abort()
  }, [load])

  const syncNow = async (full = false) => {
    if (busy) return
    setBusy(true)
    setError(null)
    setNotice(null)
    try {
      const result = await requestOnecSync(full)
      setNotice(result.created
        ? full
          ? 'Полная сверка запрошена. gold01 перечитает всю историю 1С на ближайшем запуске.'
          : 'Запрос отправлен. gold01 заберёт его в течение пяти минут.'
        : 'Запрос уже ждёт gold01; повторять не нужно.')
      await load()
    } catch (reason: unknown) {
      setError(message(reason, 'Не удалось отправить запрос на синхронизацию.'))
    } finally {
      setBusy(false)
    }
  }

  const change = async (update: { paused?: boolean; autoApply?: boolean; autoApplySales?: boolean }) => {
    if (busy) return
    setBusy(true)
    setError(null)
    setNotice(null)
    try {
      setSwitches(await updateOnecSyncSettings(update))
      await load()
    } catch (reason: unknown) {
      setError(message(reason, 'Не удалось изменить настройки синхронизации с 1С.'))
    } finally {
      setBusy(false)
    }
  }

  const open = status?.openRequest ?? null
  const requestState = open !== null && (notice === null || open.state !== 'pending')
    ? `Запрос от ${when(open.requestedAt)}: ${REQUEST_LABEL[open.state]}.`
    : notice
  return (
    <section className="space-y-4" aria-labelledby="onec-sync-settings-title">
      <h2 id="onec-sync-settings-title" className="text-[18px] font-semibold tracking-tight text-ink">Синхронизация с 1С</h2>
      {error !== null && <p role="alert" className="rounded-sm border border-red-200 bg-red-50 px-3 py-2 text-[12px] text-red-700">{error}</p>}
      {loading && <p role="status" className="rounded-lg border border-rule bg-paper p-5 text-sm text-ink-muted">Загружаем состояние синкера…</p>}
      {status !== null && (
        <div className="surface-card grid min-w-0 gap-4 rounded-md p-4 text-[13px] text-ink-2">
          <div className="flex flex-wrap items-baseline gap-x-2 gap-y-1 text-[12px]" data-testid="onec-sync-status-line">
            <span className="font-semibold text-ink" data-testid="onec-sync-health">
              {HEALTH_LABEL[status.health]}
              {status.paused && <span className="ml-2 rounded-sm bg-amber-100 px-1.5 py-0.5 text-[11px] font-semibold text-amber-800">на паузе</span>}
            </span>
            {status.heartbeat !== null && (
              <span className="text-ink-muted">
                Пульс: {when(status.heartbeat.lastHeartbeatAt)} · версия {status.heartbeat.syncerVersion}
              </span>
            )}
          </div>
          <div className="flex flex-col gap-1 sm:flex-row sm:flex-wrap sm:items-center sm:gap-2">
            <button type="button" className="btn btn-primary min-h-10 w-full sm:w-auto" onClick={() => { void syncNow() }} disabled={busy || status.paused}>
              {busy ? 'Отправляем…' : 'Синхронизировать сейчас'}
            </button>
            <div className="flex flex-wrap gap-1" role="group" aria-label="Дополнительные действия синхронизации">
              <button type="button" className="btn min-h-10 text-ink-muted" onClick={() => { void change({ paused: !status.paused }) }} disabled={busy}>
                {status.paused ? 'Возобновить' : 'Пауза'}
              </button>
              <button type="button" className="btn min-h-10 text-ink-muted" onClick={() => { void syncNow(true) }} disabled={busy || status.paused}>
                Полная сверка
              </button>
            </div>
          </div>
          {requestState !== null && (
            <p role="status" className="text-[12px] text-ink-muted" data-testid="onec-sync-request">
              {requestState}
            </p>
          )}
          {switches !== null && (
            <div className="grid gap-1 border-t border-rule pt-2">
              <button
                type="button"
                role="switch"
                aria-label="Применять все изменения из 1С автоматически"
                aria-checked={switches.autoApply}
                className="flex min-h-12 items-center justify-between gap-3 rounded-sm py-2 text-left text-[13px] text-ink-2 hover:bg-bone/60 disabled:opacity-60"
                disabled={busy}
                onClick={() => { void change({ autoApply: !switches.autoApply }) }}
              >
                <span className="min-w-0 break-words">
                  <span className="block text-ink">Применять все изменения из 1С автоматически</span>
                  <span className="mt-0.5 block text-[12px] text-ink-muted">Включая продажи.</span>
                </span>
                <SwitchTrack checked={switches.autoApply} />
              </button>
              <button
                type="button"
                role="switch"
                aria-label="Применять продажи из 1С автоматически"
                aria-checked={switches.autoApplySales}
                className="flex min-h-12 min-w-0 items-center justify-between gap-3 rounded-sm py-2 text-left text-[13px] text-ink-2 hover:bg-bone/60 disabled:opacity-60"
                disabled={busy}
                onClick={() => { void change({ autoApplySales: !switches.autoApplySales }) }}
              >
                <span className="min-w-0 break-words">
                  <span className="block text-ink">Применять продажи из 1С автоматически</span>
                  <span className="mt-0.5 block text-[12px] text-ink-muted">
                    Без расхождений в данных изделия.
                  </span>
                </span>
                <SwitchTrack checked={switches.autoApplySales} />
              </button>
              {switches.autoApply && switches.autoApplySales && (
                <p className="text-[12px] text-ink-muted">Все изменения уже включают продажи.</p>
              )}
              <details className="text-[12px] text-ink-muted" data-testid="onec-sync-audit">
                <summary className="w-fit cursor-pointer py-2">Кто изменил настройки</summary>
                <div className="grid gap-1 break-words pb-2">
                  <p data-testid="onec-auto-apply-audit">
                    Все изменения. {switches.autoApplyAt !== null && switches.autoApplyBy !== null
                      ? `${switches.autoApply ? 'Включено' : 'Выключено'}: ${switches.autoApplyBy}, ${when(switches.autoApplyAt)}`
                      : 'Выключено: каждую сверку применяете вы кнопкой «Применить»'}
                  </p>
                  <p data-testid="onec-auto-apply-sales-audit">
                    Продажи. {switches.autoApplySalesAt !== null && switches.autoApplySalesBy !== null
                      ? `${switches.autoApplySales ? 'Включено' : 'Выключено'}: ${switches.autoApplySalesBy}, ${when(switches.autoApplySalesAt)}`
                      : 'Выключено: продажи применяете вы кнопкой «Применить»'}
                  </p>
                </div>
              </details>
            </div>
          )}
          <div className="border-t border-rule pt-3" data-testid="onec-cost-coverage">
            <h3 className="text-[13px] font-semibold text-ink">Себестоимость из 1С</h3>
            <p className="mt-1 text-[12px]">
              {status.costCoverage.withCost} из {status.costCoverage.pieces} изделий
            </p>
            {Object.keys(status.costCoverage.missing).length > 0 && (
              <details className="text-[12px] text-ink-muted" data-testid="onec-cost-details">
                <summary className="w-fit cursor-pointer py-2">Почему нет себестоимости</summary>
                <ul className="grid gap-1 pb-1" aria-label="Изделия без себестоимости из 1С">
                  {Object.entries(status.costCoverage.missing)
                    .sort(([, left], [, right]) => right - left)
                    .map(([code, count]) => (
                      <li key={code}>{count} — {costMissingLabel(code)}</li>
                    ))}
                </ul>
              </details>
            )}
          </div>
          <OnecSyncDiff diff={diff} onApplied={load} />
          <details className="border-t border-rule pt-1" data-testid="onec-sync-runs">
            <summary className="cursor-pointer py-2 text-[12px] text-ink-muted">Последние запуски</summary>
            {status.recentRuns.length === 0
              ? <p className="mt-1 text-[12px] text-ink-muted">Запусков ещё не было.</p>
              : (
                <ul className="mt-2 grid gap-1.5" aria-label="Запуски синхронизации с 1С">
                  {status.recentRuns.map((run) => (
                    <li key={run.id} className="flex flex-wrap items-center justify-between gap-2 rounded-sm border border-rule bg-bone/30 px-3 py-2 text-[12px]">
                      <span>{when(run.finishedAt)} · {run.trigger === 'request' ? 'по запросу' : 'по расписанию'}</span>
                      <span className={run.outcome === 'failed' ? 'font-semibold text-red-700' : 'text-ink'}>
                        {OUTCOME_LABEL[run.outcome]}
                        {run.failureReason !== null && `: ${FAILURE_LABEL[run.failureReason]}`}
                        {run.costRows !== null && ` · партий ${run.costRows}`}
                      </span>
                      {run.operationId !== null && <Link className="text-gold underline" to="/operations">В «Операциях»</Link>}
                    </li>
                  ))}
                </ul>
              )}
          </details>
          <details className="text-[12px] text-ink-muted" data-testid="onec-sync-explanation">
            <summary className="w-fit cursor-pointer py-2">Как работает синхронизация</summary>
            <p className="max-w-prose pb-1">
              Синкер на gold01 каждые пять минут переносит из 1С закупочные цены, изделия и остатки с продажами в магазине.
              Изделия и остатки меняются после «Применить» или при включённом применении всех изменений автоматически.
              Продажи можно применять автоматически отдельно: только без расхождений в данных изделия. Остальные изменения требуют проверки.
              Если синкер замолчит, придёт уведомление в Telegram.
            </p>
          </details>
        </div>
      )}
    </section>
  )
}
